import { useState, useEffect } from "react";
import { Calendar, Copy, RefreshCw, Trash2, ExternalLink, Check, Info, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Alert, AlertDescription } from "./ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const ICalFeedManager = () => {
  const [feedData, setFeedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    fetchFeedUrl();
  }, []);

  const fetchFeedUrl = async () => {
    try {
      const response = await axios.get(`${API}/ical/feed-url`, { withCredentials: true });
      setFeedData(response.data);
    } catch (error) {
      console.error("Failed to fetch feed URL:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateFeedToken = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/ical/generate-token`, {}, { withCredentials: true });
      toast.success("Calendar feed generated!");
      fetchFeedUrl();
    } catch (error) {
      toast.error("Failed to generate feed");
    } finally {
      setGenerating(false);
    }
  };

  const revokeFeedToken = async () => {
    try {
      await axios.delete(`${API}/ical/feed-token`, { withCredentials: true });
      toast.success("Feed revoked");
      setFeedData({ has_feed: false, feed_url: null });
      setRevokeConfirm(false);
    } catch (error) {
      toast.error("Failed to revoke feed");
    }
  };

  const copyToClipboard = async () => {
    if (feedData?.feed_url) {
      await navigator.clipboard.writeText(feedData.feed_url);
      setCopied(true);
      toast.success("Feed URL copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card border-border" data-testid="ical-feed-manager">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">iCal Feed</CardTitle>
                <CardDescription>
                  Subscribe to your calendar in Apple Calendar, Outlook, or other apps
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowInstructions(true)}
              data-testid="ical-setup-guide-btn"
            >
              <Info className="w-4 h-4 mr-1" />
              Setup Guide
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {feedData?.has_feed ? (
            <>
              {/* Feed URL */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Your Calendar Feed URL</label>
                <div className="flex gap-2">
                  <Input
                    value={feedData.feed_url}
                    readOnly
                    className="font-mono text-sm bg-muted/50"
                    data-testid="ical-feed-url-input"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyToClipboard}
                    title="Copy URL"
                    data-testid="ical-copy-url-btn"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Info */}
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <Info className="w-4 h-4 text-blue-400" />
                <AlertDescription className="text-blue-200 text-sm">
                  This feed updates automatically. Your calendar app will sync new bookings and blocked times periodically.
                </AlertDescription>
              </Alert>

              {/* What's Included */}
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-2">What&apos;s included:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Confirmed bookings (📸 icon)</li>
                  <li>Pending bookings (tentative status)</li>
                  <li>Blocked time slots (🚫 icon)</li>
                  <li>Reminders 1 day before each session</li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateFeedToken}
                  disabled={generating}
                  data-testid="ical-regenerate-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
                  Regenerate URL
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => setRevokeConfirm(true)}
                  data-testid="ical-revoke-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Revoke Access
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Not Generated */}
              <div className="text-center py-4" data-testid="ical-no-feed">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  Generate a feed URL to subscribe to your 360Lumina calendar in other apps.
                </p>
                <Button onClick={generateFeedToken} disabled={generating} data-testid="ical-generate-btn">
                  {generating ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Calendar className="w-4 h-4 mr-2" />
                  )}
                  Generate Calendar Feed
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeConfirm} onOpenChange={setRevokeConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Revoke Calendar Feed?
            </DialogTitle>
            <DialogDescription>
              This will disable your current calendar feed URL. Any calendar apps using this URL will stop receiving updates. You can generate a new URL anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={revokeFeedToken}>
              Revoke Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Instructions Dialog */}
      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>How to Subscribe to Your Calendar</DialogTitle>
            <DialogDescription>
              Follow these steps to add your 360Lumina calendar to your preferred app
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Apple Calendar (macOS) */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                🍎 Apple Calendar (Mac)
              </h3>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1 ml-2">
                <li>Open <strong>Calendar</strong> app</li>
                <li>Click <strong>File</strong> → <strong>New Calendar Subscription...</strong></li>
                <li>Paste your feed URL</li>
                <li>Click <strong>Subscribe</strong></li>
                <li>Set refresh frequency (recommended: Every hour)</li>
                <li>Click <strong>OK</strong></li>
              </ol>
            </div>

            {/* Apple Calendar (iOS) */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                📱 Apple Calendar (iPhone/iPad)
              </h3>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1 ml-2">
                <li>Open <strong>Settings</strong> → <strong>Calendar</strong> → <strong>Accounts</strong></li>
                <li>Tap <strong>Add Account</strong> → <strong>Other</strong></li>
                <li>Tap <strong>Add Subscribed Calendar</strong></li>
                <li>Paste your feed URL</li>
                <li>Tap <strong>Next</strong> → <strong>Save</strong></li>
              </ol>
            </div>

            {/* Google Calendar */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                📅 Google Calendar
              </h3>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1 ml-2">
                <li>Open <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">calendar.google.com</a></li>
                <li>Click the <strong>+</strong> next to &quot;Other calendars&quot;</li>
                <li>Select <strong>From URL</strong></li>
                <li>Paste your feed URL</li>
                <li>Click <strong>Add calendar</strong></li>
              </ol>
              <p className="text-xs text-muted-foreground ml-2">
                Note: Google Calendar updates subscribed calendars every 12-24 hours.
              </p>
            </div>

            {/* Outlook */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                📧 Outlook (Desktop & Web)
              </h3>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1 ml-2">
                <li>Open Outlook Calendar</li>
                <li>Click <strong>Add calendar</strong> → <strong>Subscribe from web</strong></li>
                <li>Paste your feed URL</li>
                <li>Give it a name (e.g., &quot;360Lumina Bookings&quot;)</li>
                <li>Click <strong>Import</strong></li>
              </ol>
            </div>

            {/* Other Apps */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                🔗 Other Calendar Apps
              </h3>
              <p className="text-sm text-muted-foreground ml-2">
                Most calendar apps support iCal subscriptions. Look for options like:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 ml-4">
                <li>&quot;Add calendar from URL&quot;</li>
                <li>&quot;Subscribe to calendar&quot;</li>
                <li>&quot;Import ICS/iCal&quot;</li>
              </ul>
            </div>

            {/* Security Note */}
            <Alert className="bg-yellow-500/10 border-yellow-500/30">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              <AlertDescription className="text-yellow-200 text-sm">
                <strong>Security Tip:</strong> Your feed URL contains a private token. Don&apos;t share it publicly. If compromised, click &quot;Regenerate URL&quot; to create a new one.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowInstructions(false)}>Got it!</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ICalFeedManager;
